import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { authApi } from "../services/api";
import Logo from "../components/Logo";
import Button from "../components/Button";
import Input from "../components/Input";
import StepBar from "../components/StepBar";
import Callout from "../components/Callout";
import LanguageSwitcher from "../components/LanguageSwitcher";

const T = { night: "#0A1F3D", royal: "#1263B0", cyan: "#1CBCD3", gold: "#C9922A" };
const CODE_VALID_MINUTES = 10;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState(Array(6).fill(""));
  const [resetToken, setResetToken] = useState("");
  const [passwords, setPasswords] = useState({ newPassword: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const codeRefs = Array(6).fill(null).map(() => useRef());

  const handleCode = (i, v) => {
    const n = [...code]; n[i] = v.replace(/\D/g, "").slice(-1); setCode(n);
    if (v && i < 5) codeRefs[i + 1].current?.focus();
  };

  const handlePwd = e => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  // --- Etape 1 : identification ---
  const submitIdentifier = async ev => {
    ev.preventDefault();
    if (!identifier.trim()) {
      setErrors({ identifier: t("forgot_password.identifier_required") });
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await authApi.forgotPassword({ identifier: identifier.trim() });
      toast.success(t("forgot_password.sent_toast"));
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.message || t("forgot_password.generic_error"));
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      await authApi.forgotPassword({ identifier: identifier.trim() });
      toast.success(t("forgot_password.resend_toast"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("forgot_password.generic_error"));
    } finally { setLoading(false); }
  };

  // --- Etape 2 : verification du code ---
  const submitCode = async ev => {
    ev.preventDefault();
    if (code.some(v => !v)) { toast.error(t("forgot_password.code_incomplete")); return; }
    setLoading(true);
    try {
      const res = await authApi.verifyResetCode({
        identifier: identifier.trim(), code: code.join(""),
      });
      setResetToken(res.data.data.resetToken);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || t("forgot_password.verify_error"));
    } finally { setLoading(false); }
  };

  // --- Etape 3 : nouveau mot de passe ---
  const validatePasswords = () => {
    const e = {};
    const pwd = passwords.newPassword;
    const strongEnough = pwd.length >= 8 && /[0-9]/.test(pwd) && /[a-zA-Z]/.test(pwd);
    if (!strongEnough) e.newPassword = t("forgot_password.password_error");
    if (passwords.newPassword !== passwords.confirmPassword) {
      e.confirmPassword = t("forgot_password.confirm_error");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitReset = async ev => {
    ev.preventDefault();
    if (!validatePasswords()) return;
    setLoading(true);
    try {
      await authApi.resetPassword({
        token: resetToken, newPassword: passwords.newPassword,
      });
      toast.success(t("forgot_password.reset_success_toast"));
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.message || t("forgot_password.reset_error"));
    } finally { setLoading(false); }
  };

  const stepTitles = [
    t("forgot_password.step1_title"),
    t("forgot_password.step2_title"),
    t("forgot_password.step3_title"),
  ];

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "#F7F9FC",
      padding: "32px 24px", fontFamily: "'DM Sans',sans-serif" }}>
      <div style={{ width: "100%", maxWidth: "480px" }}>
        <div style={{ marginBottom: "32px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Logo size="md" />
            <LanguageSwitcher />
          </div>
          <h1 style={{ fontSize: "26px", fontWeight: 800, color: T.night,
            marginTop: "24px", marginBottom: "4px" }}>
            {t("forgot_password.title")}</h1>
          <p style={{ color: "#64748B", fontSize: "13px", marginBottom: "20px" }}>
            {stepTitles[step]}</p>
          <StepBar total={3} current={step} />
        </div>

        {/* Etape 0 - Identification */}
        {step === 0 && (
          <form onSubmit={submitIdentifier}>
            <Callout variant="cyan" icon="🔐">
              {t("forgot_password.identifier_info")}
            </Callout>
            <Input label={t("forgot_password.identifier_label")} name="identifier"
              placeholder={t("forgot_password.identifier_placeholder")}
              value={identifier}
              onChange={e => { setIdentifier(e.target.value); if (errors.identifier) setErrors({}); }}
              error={errors.identifier} required />
            <Button type="submit" variant="cyan" full loading={loading}>
              {t("forgot_password.submit")}
            </Button>
            <p style={{ textAlign: "center", marginTop: "16px", fontSize: "13px", color: "#64748B" }}>
              <Link to="/login" style={{ color: T.cyan, fontWeight: 600, textDecoration: "none" }}>
                {t("forgot_password.back_to_login")}</Link></p>
          </form>
        )}

        {/* Etape 1 - Code */}
        {step === 1 && (
          <form onSubmit={submitCode}>
            <Callout variant="cyan" icon="📧">
              {t("forgot_password.code_info", { minutes: CODE_VALID_MINUTES })}
            </Callout>
            <p style={{ textAlign: "center", fontSize: "13px", fontWeight: 600,
              color: "#334155", marginBottom: "16px" }}>
              {t("forgot_password.code_label")}</p>
            <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "24px" }}>
              {code.map((v, i) => (
                <input key={i} ref={codeRefs[i]} value={v} inputMode="numeric"
                  onChange={e => handleCode(i, e.target.value)}
                  onKeyDown={e => { if (e.key === "Backspace" && !v && i > 0) codeRefs[i - 1].current?.focus(); }}
                  maxLength={1}
                  style={{ width: "52px", height: "60px", textAlign: "center",
                    fontSize: "26px", fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
                    border: `2.5px solid ${v ? T.cyan : "#E2E8F0"}`,
                    borderRadius: "12px", color: T.night, background: "#fff", outline: "none",
                    transition: "border-color 0.15s" }} />
              ))}
            </div>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <span style={{ fontSize: "13px", color: "#94A3B8" }}>
                {t("forgot_password.code_resend")} </span>
              <span onClick={resendCode}
                style={{ fontSize: "13px", color: T.cyan, fontWeight: 600, cursor: "pointer" }}>
                {t("forgot_password.resend")}</span>
            </div>
            <Button type="submit" variant="cyan" full loading={loading}
              disabled={code.some(v => !v)}>{t("forgot_password.verify")}</Button>
            <p style={{ textAlign: "center", marginTop: "16px", fontSize: "13px", color: "#64748B" }}>
              <Link to="/login" style={{ color: T.cyan, fontWeight: 600, textDecoration: "none" }}>
                {t("forgot_password.back_to_login")}</Link></p>
          </form>
        )}

        {/* Etape 2 - Nouveau mot de passe */}
        {step === 2 && (
          <form onSubmit={submitReset}>
            <Callout variant="gold" icon="✅">
              {t("forgot_password.step2_title")} ✓
            </Callout>
            <Input label={t("forgot_password.new_password_label")} name="newPassword" type="password"
              placeholder={t("forgot_password.new_password_placeholder")}
              value={passwords.newPassword} onChange={handlePwd}
              error={errors.newPassword} required />
            <Input label={t("forgot_password.confirm_password_label")} name="confirmPassword" type="password"
              placeholder={t("forgot_password.confirm_password_placeholder")}
              value={passwords.confirmPassword} onChange={handlePwd}
              error={errors.confirmPassword} required />
            <Button type="submit" variant="gold" full loading={loading}>
              {t("forgot_password.reset_submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
